import { useCallback } from 'react';
import { useGateValue } from '@statsig/react-bindings';

import { V1WorkspaceType } from '@/generated/hooks/agora/models/v1WorkspaceType';
import { WorkspaceDomain } from '@/modules/onboarding/constants';
import { FEATURE_FLAGS } from '@/shared/types/features';

/**
 * Maps the purpose(s) picked on a workspace-creation form to the
 * `workspace_type` to send, or `undefined` when the field should be omitted.
 * Accepts a single value (the create-workspace dialog's dropdown) or a list (the
 * onboarding domain chips, which are multi-select).
 */
export type TWorkspaceTypeResolver = (
  purpose?: string | string[] | null,
) => V1WorkspaceType | undefined;

/**
 * The counterpart of `useIsFinanceWorkspace` ("is the finance experience active
 * here?") for creation: "should this new workspace be a finance one?". Every
 * surface that creates a workspace — the in-app dialog and the onboarding step —
 * shares this one determination.
 *
 * Finance is offered on both surfaces unconditionally, but it only types the
 * workspace when the `enable_finance_workspace` gate is on: a finance-typed
 * workspace whose finance experience (sidebar nav, CFO landing, /finance routes)
 * is gated off for the creator would be one they can't use as intended. With the
 * gate off the pick stays purely descriptive, like every other purpose. Folding
 * the gate in here — the same reasoning as `useIsFinanceWorkspace` — means a new
 * creation surface can't forget it.
 *
 * Purposes are matched against the `WorkspaceDomain` labels, the values both
 * forms hold. Finance is the only one that maps to a type: the backend models no
 * other, so every other purpose is descriptive and isn't persisted today.
 */
export function useWorkspaceTypeResolver(): TWorkspaceTypeResolver {
  const isFinanceWorkspaceEnabled = useGateValue(
    FEATURE_FLAGS.ENABLE_FINANCE_WORKSPACE,
  );

  return useCallback(
    purpose => {
      const purposes = Array.isArray(purpose) ? purpose : [purpose];

      return isFinanceWorkspaceEnabled &&
        purposes.includes(WorkspaceDomain.FINANCE)
        ? V1WorkspaceType.WORKSPACE_TYPE_FINANCE
        : undefined;
    },
    [isFinanceWorkspaceEnabled],
  );
}
